import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiClipboard, FiUsers } from 'react-icons/fi';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { FormCheck } from '@/components/custom/form';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { PersonalizedProjectRowTeacher } from './PersonalizedProjectRowTeacher';

const welcomeMessage = 'Acompanhe aqui as entregas deste grupo! Aprove as versões em andamento e veja quem está participando de cada uma. 📋';

// Detalhe de um grupo de projetos (rota /projetos/grupos/:id) no tema
// playful do professor — mesmo card grande com gradiente usado em Redação
// (essay/PlayfulTeacher/index.jsx) e mesma linha já usada na aba Personalizados
// (PersonalizedProjectRowTeacher.jsx) pra listar as versões do grupo — aqui
// sem a opção de editar (não existe editar uma versão isolada) — vai direto
// pro workspace do projeto (`/projetos/:id`, sem passar pela tela "sobre" que
// o tema default ainda usa), levando esta `uri` via `backTo` pro botão de
// voltar pro grupo lá. `onDeleteProject` exclui só a versão clicada (endpoint
// `projects/:id`), não o grupo inteiro.
// Busca/filtro/exclusão continuam em ViewGroupProject/index.jsx
// (ScreenContentGroupProject), que só passa os dados já prontos pra cá.
export function GroupProjectDetailPlayfulTeacher({ uri, groupId, groupedProject, participantsQuantity, filters, filteredProjects, loading, onDeleteProject }) {
  const { t } = useTranslation();

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/projetos/grupos',
        name: 'Projetos',
        isActive: false
      },
      {
        route: uri,
        name: `Grupos de ${groupedProject?.projects?.[0]?.name}`,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={215}
        imageOffset={200}
        bubbleCorner="tl"
        bubbleMaxWidth={420}
        text={welcomeMessage}
      />

      <section
        className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: 'trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
        />

        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6">
            <FormCheck
              label={t('metrics.approvedProjects')}
              checked={filters.values.approvedProjects}
              onCheckedChange={checked => {
                filters.setFieldValue('approvedProjects', checked);
                filters.setFieldValue('projectsInProgress', false);
              }}
            />
            <FormCheck
              label={t('metrics.projectsInProgress')}
              checked={filters.values.projectsInProgress}
              onCheckedChange={checked => {
                filters.setFieldValue('projectsInProgress', checked);
                filters.setFieldValue('approvedProjects', false);
              }}
            />
          </div>

          <div className="flex items-center gap-4 rounded-[16px] border border-neutral-100 bg-white px-4 py-3 shadow-card">
            <div className="flex items-center gap-2">
              <FiClipboard className="h-4 w-4 text-mission" />
              <span
                className="text-sm font-bold"
                style={{ color: '#222731' }}
              >
                {groupedProject?.projects?.length} {t('projects.sidebarGroups')}
              </span>
            </div>

            <div className="h-6 w-px bg-neutral-100" />

            <div className="flex items-center gap-2">
              <FiUsers className="h-4 w-4 text-mission" />
              <span
                className="text-sm font-bold"
                style={{ color: '#222731' }}
              >
                {participantsQuantity} {t('sideBar.participants')}
              </span>
            </div>
          </div>
        </div>

        <hr className="relative my-6 border-t border-neutral-100" />

        {loading && <Loader />}

        {!loading && filteredProjects?.length === 0 && (
          <EmptyState
            type="project"
            title={t('projects.newProjects')}
            text={t('emptyState.createNewProjectInteract')}
          />
        )}

        {!loading && filteredProjects?.length > 0 && (
          <div className="relative flex flex-col gap-3">
            {filteredProjects.map((project, index) => (
              <PersonalizedProjectRowTeacher
                key={project.id}
                project={project}
                groupId={groupId}
                index={index}
                onDelete={() => onDeleteProject(project.id)}
                backTo={uri}
                showEditOption={false}
              />
            ))}
          </div>
        )}
      </section>
    </DashboardContent>
  );
}
